import React, { useState, useEffect } from "react";
import {
  Box,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

import ReportHeader from "./ReportHeader";
import ReportFilters from "./ReportFilters";
import ReportResults from "./ReportResults";
import VTSReportsView from "./VTSReportsView";
import { REPORTS_DETECTION_TYPES, AREA_TOKEN_TO_LOCATION } from "../../utils/constants";
import { fetchReportDataFromEndpoints } from "./ReportDataService";
import {
  API_TO_APP_DETECTION_MAP,
  APP_TO_API_DETECTION_MAP,
  AREA_ENDPOINTS,
} from "./ReportConstants";
import { isAdminUser, getLoggedInArea } from "../../utils/authService";

const ReportsView = ({ onBackToLive }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // ── Report mode: 'va' (Video Analytics) or 'vts' (Vehicle Tracking) ──────
  const [reportMode, setReportMode] = useState("va");

  const handleModeChange = (_e, newMode) => {
    if (!newMode) return;
    setReportMode(newMode);
    // Reset VA view to filter page when switching away and back
    if (newMode === "va") setViewMode("filter");
  };

  // ✅ Get user authentication info
  const userIsAdmin = isAdminUser();
  const userArea = getLoggedInArea();
  
  // ✅ Initialize selectedAreas based on user type
  const getInitialAreas = () => {
    if (!userIsAdmin && userArea) {
      console.log('🔒 Area-specific user report view - initialized to:', userArea);
      return [userArea];
    }
    console.log('🔑 Admin user report view - can access all areas');
    return ["All"];
  };

  const [viewMode, setViewMode] = useState("filter"); 
  const [selectedAreas, setSelectedAreas] = useState(getInitialAreas());
  const [selectedDetections, setSelectedDetections] = useState(["All"]);
  const [selectedLocationTypes, setSelectedLocationTypes] = useState(["All"]); 
  const [locationTypeValues, setLocationTypeValues] = useState(["ALL"]); 

  const [selectedPriorities, setSelectedPriorities] = useState(["All"]);
  const [selectedCloseStatus, setSelectedCloseStatus] = useState(["All"]); // NEW: Close status filter

  const [timeFilter, setTimeFilter] = useState({
    startTime: new Date(new Date().setHours(0, 0, 0, 0)),
    endTime: new Date(new Date().setHours(23, 59, 59, 999)),
    isApplied: false,
  });

  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 100,
    hasMore: true,
  });

  const [typePagination, setTypePagination] = useState({
    cd: { page: 1, hasMore: true },
    pd: { page: 1, hasMore: true },
    id: { page: 1, hasMore: true },
    vd: { page: 1, hasMore: true },
    vc: { page: 1, hasMore: true },
    ls: { page: 1, hasMore: true }, 
    us: { page: 1, hasMore: true }, 
    ct: { page: 1, hasMore: true }, 
    sh: { page: 1, hasMore: true },
    ii: { page: 1, hasMore: true },
    or: { page: 1, hasMore: true }, 
    ta: { page: 1, hasMore: true } 
  });

  // State for data
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // FIXED: isTypeLoadingMore state - Added 'id' type
  const [isTypeLoadingMore, setIsTypeLoadingMore] = useState({
    cd: false,
    pd: false,
    id: false,
    vd: false,
    vc: false,
    ls: false,
    us: false,
    ct: false, 
    sh: false,
    ii: false,
    or: false,
    ta: false
  });
  
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [totalEvents, setTotalEvents] = useState(0);

  const [typeSpecificCounts, setTypeSpecificCounts] = useState({
    cd: 0,
    pd: 0,
    id: 0,
    vd: 0,
    vc: 0,
    ls: 0,
    us: 0, 
    ct: 0,
    sh: 0,
    ii: 0,
    or: 0,
    ta: 0
  });

  const [loadedEventIds, setLoadedEventIds] = useState(new Set());
  const [typeLoadedEventIds, setTypeLoadedEventIds] = useState({
    cd: new Set(),
    pd: new Set(),
    id: new Set(),
    vd: new Set(),
    vc: new Set(),
    ls: new Set(), 
    sh: new Set(),
    ii: new Set(),
    ct: new Set(),
    or: new Set(),
    ta: new Set()
  });

  const [reportData, setReportData] = useState(() => {
    const initialData = {};
    ['cd', 'pd', 'id', 'vd', 'vc', 'ls','ct','sh','ii','or','ta'].forEach((type) => {
      initialData[type] = [];
    });
    return initialData;
  });

  const getEventTypesForDetection = (detectionType) => {
    if (detectionType === 'ls') {
      console.log('🔍 Loading Status selected - returning both LS and US event types');
      return ["LS", "US"];
    }

    const apiEventType = APP_TO_API_DETECTION_MAP[detectionType];
    
    if (apiEventType) {
      console.log(`🔍 Detection '${detectionType}' maps to event type:`, [apiEventType]);
      return [apiEventType];
    } else {
      console.warn(`🔍 No mapping found for detection type '${detectionType}', falling back to all types`);
      return ["CD", "PD", "ID", "VD", "VC", "LS", "US", "CT", "SH", "II"];
    }
  };

  const getEventTypes = (selectedDetectionValues) => {
  if (selectedDetectionValues.includes("All") || selectedDetectionValues.length === 0) {
    console.log('🔍 "All Types" selected - returning all event types INCLUDING vehicle alerts (OR, TA)');
    // Include OR and TA so ReportDataService.jsx can separate them and fetch from correct endpoints
    return ["CD", "PD", "ID", "VD", "VC", "LS", "US", "CT", "SH", "II", "OR", "TA"];
  }

  if (selectedDetectionValues.includes("ls")) {
    console.log('🔍 Loading Status included - ensuring both LS and US are included');
    selectedDetectionValues = selectedDetectionValues.filter(type => type !== "ls");
    if (!selectedDetectionValues.includes("LS")) selectedDetectionValues.push("LS");
    if (!selectedDetectionValues.includes("US")) selectedDetectionValues.push("US");
  }

  let eventTypes = [];
  selectedDetectionValues.forEach((detection) => {
    const apiEventType = APP_TO_API_DETECTION_MAP[detection];
    if (apiEventType) {
      console.log(`🔍 Adding event type for '${detection}':`, apiEventType);
      eventTypes.push(apiEventType);
    }
  });

  eventTypes = [...new Set(eventTypes)];
  
  console.log('🔍 Final event types to fetch:', eventTypes);

  if (eventTypes.length === 0) {
    console.log('🔍 No valid event types found, falling back to all types INCLUDING vehicle alerts');
    return ["CD", "PD", "ID", "VD", "VC", "LS", "US", "CT", "SH", "II", "OR", "TA"];
  }

  return eventTypes;
};

  const processReportData = (data) => {
  const organizedData = {};

  REPORTS_DETECTION_TYPES.forEach((type) => {
    organizedData[type.id] = [];
  });

  data.forEach((event) => {
    const apiEventType = event.detectionType || event.eventType;
    
    let targetDetectionType = null;
    
    switch(apiEventType) {
      case 'CD':
        targetDetectionType = 'cd';
        break;
      case 'PD':
        targetDetectionType = 'pd';  
        break;
      case 'ID':
        targetDetectionType = 'id'; 
        break;
      case 'VD':
        targetDetectionType = 'vd';
        break;
      case 'VC':
        targetDetectionType = 'vc';
        break;
      case 'LS':
      case 'US':
        targetDetectionType = 'ls';  
        break;
       case 'CT':
        targetDetectionType = 'ct'; 
        break;
      case 'SH':
        targetDetectionType = 'sh';  
        break;
      case 'II':
        targetDetectionType = 'ii';
        break;
      case 'OR':
        targetDetectionType = 'or'; // Off-route
        console.log(`📦 Processing OR (Off-Route) event:`, event);
        break;
      case 'TA':
        targetDetectionType = 'ta'; // Tamper
        console.log(`📦 Processing TA (Tamper) event:`, event);
        break;
      default:
        console.warn(`Unknown API event type: ${apiEventType}`);
        return;
    }

    if (targetDetectionType && organizedData[targetDetectionType]) {
      const processedEvent = {
        ...event,
        detectionType: apiEventType,
        mappedType: targetDetectionType
      };
      
      organizedData[targetDetectionType].push(processedEvent);
      
      console.log(`🔄 Added ${apiEventType} event to ${targetDetectionType} card:`, {
        eventId: event.eventId || event.id,
        location: event.location,
        isVehicleAlert: event.isVehicleAlert,
        alertType: event.alertType,
        vehicleNumber: event.vehicleNumber
      });
    }
  });

  return organizedData;
};

  const fetchReportData = async (
    isLoadingMore = false,
    specificDetectionType = null
  ) => {
    if (!timeFilter.isApplied) return;

    const isTypeSpecificLoad = specificDetectionType !== null;

    // Set loading states
    if (isTypeSpecificLoad) {
      setIsTypeLoadingMore((prev) => ({
        ...prev,
        [specificDetectionType]: true,
      }));
    } else if (isLoadingMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setError(null);
      setDataLoaded(false);
      setLoadedEventIds(new Set());
      setTypeLoadedEventIds({
        cd: new Set(),
        pd: new Set(),
        id: new Set(),
        vd: new Set(),
        vc: new Set(),
        ls: new Set(),
        us: new Set(),
        ct: new Set(),
        sh: new Set(),
        ii: new Set(),
        or: new Set(),
        ta: new Set()
      });
    }

    try {
      const targetAreas = getTargetAreas(selectedAreas);

      const eventTypes = isTypeSpecificLoad
        ? getEventTypesForDetection(specificDetectionType)
        : getEventTypes(selectedDetections);

      console.log('🔍 Event Types to fetch:', eventTypes, 'for detections:', isTypeSpecificLoad ? [specificDetectionType] : selectedDetections);
      console.log('🔍 Close Status Filter:', selectedCloseStatus); 

      const currentPage = isTypeSpecificLoad
        ? typePagination[specificDetectionType].page
        : pagination.page;

      const response = await fetchReportDataFromEndpoints({
        targetAreas,
        eventTypes,
        currentPage,
        timeFilter,
        loadedEventIds: isTypeSpecificLoad
          ? typeLoadedEventIds[specificDetectionType]
          : loadedEventIds,
        locationTypeValues, 
        selectedPriorities,
        selectedCloseStatus, 
      });

      const { newEvents, totalEventsCount, typeCountsFromFetch } = response;

      let openEvents = 0;
      let closedEvents = 0;

      newEvents.forEach((event) => {
        if (event.isClosed || event.closeStatus === 'Closed') {
          closedEvents++;
        } else {
          openEvents++;
        }
      });

      // NEW: Log close status distribution
      console.log('📊 Close Status Distribution:', {
        open: openEvents,
        closed: closedEvents,
        total: newEvents.length,
        filterApplied: selectedCloseStatus
      });

      if (!isTypeSpecificLoad) {
        setTotalEvents(totalEventsCount);
      }

      if (!isLoadingMore && !isTypeSpecificLoad) {
        setTypeSpecificCounts({
          cd: typeCountsFromFetch.cd,
          pd: typeCountsFromFetch.pd,
          id: typeCountsFromFetch.id,
          vd: typeCountsFromFetch.vd,
          vc: typeCountsFromFetch.vc,
          ls: typeCountsFromFetch.ls,
          us: typeCountsFromFetch.us,
          ct: typeCountsFromFetch.ct,
          sh: typeCountsFromFetch.sh,
          ii: typeCountsFromFetch.ii,
          or: typeCountsFromFetch.or,
          ta: typeCountsFromFetch.ta
        });
      }

      updatePaginationInfo(
        newEvents,
        isTypeSpecificLoad,
        specificDetectionType,
        totalEventsCount
      );

      updateLoadedEventIds(
        newEvents,
        isTypeSpecificLoad,
        specificDetectionType
      );

      updateReportData(
        newEvents,
        isTypeSpecificLoad,
        specificDetectionType,
        isLoadingMore
      );

      setDataLoaded(true);

      if (newEvents.length === 0 && !isLoadingMore && !isTypeSpecificLoad) {
        const filterDesc = selectedCloseStatus.includes("All") ? "selected filters" : 
                          selectedCloseStatus.includes("Closed") ? "closed events filter" : 
                          selectedCloseStatus.includes("Open") ? "open events filter" : "selected filters";
        setError(`No data found for the ${filterDesc}.`);
      }
    } catch (err) {
      console.error("Error fetching report data:", err);
      setError(`Failed to fetch report data: ${err.message}`);
    } finally {

      if (isTypeSpecificLoad) {
        setIsTypeLoadingMore((prev) => ({
          ...prev,
          [specificDetectionType]: false,
        }));
      } else if (isLoadingMore) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const filterEventsByPriority = (events, selectedPriorityValues) => {
    if (selectedPriorityValues.includes("All") || selectedPriorityValues.length === 0) {
      return events;
    }
    
    return events.filter(event => {
      if (!event.severity) {
        return selectedPriorityValues.includes("Low") || selectedPriorityValues.includes("Unassigned");
      }
      
      return selectedPriorityValues.includes(event.severity);
    });
  };

  const updatePaginationInfo = (
    newEvents,
    isTypeSpecificLoad,
    specificDetectionType,
    totalEventsCount
  ) => {
    if (isTypeSpecificLoad) {
      const currentLoadedCount =
        typeLoadedEventIds[specificDetectionType].size +
        newEvents.filter(
          (e) =>
            API_TO_APP_DETECTION_MAP[e.detectionType] === specificDetectionType
        ).length;

      const hasMore =
        currentLoadedCount < typeSpecificCounts[specificDetectionType];
      setTypePagination((prev) => ({
        ...prev,
        [specificDetectionType]: {
          page: prev[specificDetectionType].page,
          hasMore,
        },
      }));
    } else {
      const currentlyLoadedCount = isLoadingMore
        ? loadedEventIds.size + newEvents.length
        : newEvents.length;

      const hasMore = currentlyLoadedCount < totalEventsCount;
      setPagination((prev) => ({ ...prev, hasMore }));
    }
  };

  const updateLoadedEventIds = (
    newEvents,
    isTypeSpecificLoad,
    specificDetectionType
  ) => {
    if (isTypeSpecificLoad) {
      setTypeLoadedEventIds((prev) => {
        const updatedIds = new Set(prev[specificDetectionType]);
        newEvents.forEach((event) => {
          if (
            API_TO_APP_DETECTION_MAP[event.detectionType] ===
            specificDetectionType
          ) {
            updatedIds.add(event.id);
          }
        });
        return { ...prev, [specificDetectionType]: updatedIds };
      });
    } else {
      const updatedEventIds = new Set(loadedEventIds);
      newEvents.forEach((event) => updatedEventIds.add(event.id));
      setLoadedEventIds(updatedEventIds);

      setTypeLoadedEventIds((prev) => {
        const updated = { ...prev };
        newEvents.forEach((event) => {
          const type = API_TO_APP_DETECTION_MAP[event.detectionType];
          if (type && updated[type]) {
            updated[type].add(event.id);
          }
        });
        return updated;
      });
    }
  };

  const updateReportData = (
    newEvents,
    isTypeSpecificLoad,
    specificDetectionType,
    isLoadingMore
  ) => {
    if (isTypeSpecificLoad) {
      setReportData((prev) => {
        const newData = { ...prev };
        const filteredEvents = newEvents.filter(
          (e) => API_TO_APP_DETECTION_MAP[e.detectionType] === specificDetectionType
        );
        newData[specificDetectionType] = [
          ...newData[specificDetectionType],
          ...filteredEvents,
        ];
        return newData;
      });
    } else if (isLoadingMore) {
      // Append new data
      const newReportData = { ...reportData };
      const organizedNewData = processReportData(newEvents);

      REPORTS_DETECTION_TYPES.forEach((type) => {
        newReportData[type.id] = [
          ...newReportData[type.id],
          ...organizedNewData[type.id],
        ];
      });

      setReportData(newReportData);
    } else {
      setReportData(processReportData(newEvents));
    }
  };

  const getTargetAreas = (selectedAreaValues) => {
    const areaValues = getAreaValues(selectedAreaValues);
    const isAllAreas = areaValues.includes("All") || areaValues.length === 0;

    let targetAreas = [];

    if (isAllAreas) {
      targetAreas = Object.entries(AREA_ENDPOINTS).map(
        ([areaName, details]) => ({
          name: areaName,
          endpoint: details.endpoint,
          groupId: details.groupId,
        })
      );
    } else {
      // Include selected areas
      for (const areaValue of areaValues) {
        let areaName =
          typeof areaValue === "object" ? areaValue.value : areaValue;

        // Convert token format (bokaro-kargali) to display name (Bokaro & Kargali)
        if (AREA_TOKEN_TO_LOCATION[areaName.toLowerCase()]) {
          areaName = AREA_TOKEN_TO_LOCATION[areaName.toLowerCase()];
        }

        // Direct lookup
        if (AREA_ENDPOINTS[areaName]) {
          targetAreas.push({
            name: areaName,
            endpoint: AREA_ENDPOINTS[areaName].endpoint,
            groupId: AREA_ENDPOINTS[areaName].groupId,
          });
        }
      }

      if (targetAreas.length === 0) {
        for (const areaValue of areaValues) {
          let areaName =
            typeof areaValue === "object" ? areaValue.value : areaValue;

          // Convert token format to display name
          if (AREA_TOKEN_TO_LOCATION[areaName.toLowerCase()]) {
            areaName = AREA_TOKEN_TO_LOCATION[areaName.toLowerCase()];
          }

          for (const [area, details] of Object.entries(AREA_ENDPOINTS)) {
            if (
              areaName.toLowerCase().includes(area.toLowerCase()) ||
              area.toLowerCase().includes(areaName.toLowerCase())
            ) {
              targetAreas.push({
                name: area,
                endpoint: details.endpoint,
                groupId: details.groupId,
              });
              break;
            }
          }
        }
      }

      if (targetAreas.length === 0) {
        console.warn(`⚠️ No matching areas found for: ${areaValues.join(', ')}. Available areas:`, Object.keys(AREA_ENDPOINTS));
        console.warn('🔴 No areas could be matched. Returning empty array.');
        // Don't default to Kuju - return empty array so user can see the error
        targetAreas = [];
      }
    }

    return targetAreas;
  };

  const getAreaValues = (selectedAreaValues) => {
    if (!Array.isArray(selectedAreaValues)) {
      return selectedAreaValues === "All" ? ["All"] : [selectedAreaValues];
    }

    if (selectedAreaValues.length === 0 || selectedAreaValues.includes("All")) {
      return ["All"];
    }

    return selectedAreaValues.map((area) =>
      typeof area === "object" && area.value ? area.value : area
    );
  };

  const handleLoadMore = () => {
    setPagination((prev) => ({
      ...prev,
      page: prev.page + 1,
    }));

    fetchReportData(true);
  };

  const handleTypeSpecificLoadMore = (detectionType) => {
    setTypePagination((prev) => ({
      ...prev,
      [detectionType]: {
        ...prev[detectionType],
        page: prev[detectionType].page + 1,
      },
    }));

    fetchReportData(true, detectionType);
  };

  const handleApplyFilters = (filters) => {
    console.log('🔍 Applied filters:', filters);
    
    // ✅ Enforce area restriction for non-admin users
    let finalAreas = filters.areas;
    if (!userIsAdmin && userArea) {
      console.log('🔒 Area-specific user - enforcing area restriction to:', userArea);
      finalAreas = [userArea];
    }
    
    setSelectedDetections(filters.detectionTypes);
    setSelectedAreas(finalAreas);
    setSelectedPriorities(filters.priorities);
    setSelectedLocationTypes(filters.locationTypes);
    setLocationTypeValues(filters.locationTypeValues);
    setSelectedCloseStatus(filters.closeStatus); 
    setTimeFilter({
      ...filters.timeFilter,
      isApplied: true,
    });

    setPagination({
      page: 1,
      perPage: 100,
      hasMore: true,
    });

    setTypePagination({
      cd: { page: 1, hasMore: true },
      pd: { page: 1, hasMore: true },
      id: { page: 1, hasMore: true },  
      vd: { page: 1, hasMore: true },
      vc: { page: 1, hasMore: true },
      ls: { page: 1, hasMore: true },
      us: { page: 1, hasMore: true },
      ct: { page: 1, hasMore: true },
      sh: { page: 1, hasMore: true },
      ii: { page: 1, hasMore: true }
    });

    setLoadedEventIds(new Set());
    setTypeLoadedEventIds({
      cd: new Set(),
      pd: new Set(),
      id: new Set(),
      vd: new Set(),
      vc: new Set(),
      ls: new Set(), 
      ct: new Set(),
      sh: new Set(),
      ii: new Set(),
    });    setViewMode("results");
  };

  const handleBackToFilter = () => {
    setViewMode("filter");
  };

  useEffect(() => {
    if (viewMode === "results" && timeFilter.isApplied) {
      fetchReportData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, timeFilter.isApplied]);


  const updateEventPriority = async (eventId, priorityData) => {
  };

  const getEventPriority = (eventId) => {
    return null;
  };


  const onEventUpdated = (updatedEvent) => {

  };

  return (
    <Box
      sx={{
        p: 0,
        backgroundColor: "#17212b",
        color: "white",
        height: "100%",
        overflowY: "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── VA / VTS mode switcher ────────────────────────────────────── */}
      <Box
        sx={{
          flexShrink: 0,
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.18) 100%)",
          px: { xs: 1.5, sm: 3 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {/* VA card */}
        {[
          {
            value: "va",
            icon: <VideocamIcon sx={{ fontSize: 22 }} />,
            label: "Video Analytics",
            short: "VA",
            desc: "Intrusion, tamper, stoppage & more",
            accent: "#1ABC9C",
          },
          {
            value: "vts",
            icon: <LocalShippingIcon sx={{ fontSize: 22 }} />,
            label: "Vehicle Tracking",
            short: "VTS",
            desc: "Off-route, overspeed, tamper alerts",
            accent: "#3B82F6",
          },
        ].map((tab) => {
          const active = reportMode === tab.value;
          return (
            <Box
              key={tab.value}
              onClick={() => handleModeChange(null, tab.value)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: { xs: 1.5, sm: 2.5 },
                py: 1,
                borderRadius: "10px",
                cursor: "pointer",
                border: active
                  ? `1.5px solid ${tab.accent}`
                  : "1.5px solid rgba(255,255,255,0.08)",
                background: active
                  ? `linear-gradient(135deg, ${tab.accent}22 0%, ${tab.accent}0d 100%)`
                  : "rgba(255,255,255,0.03)",
                boxShadow: active
                  ? `0 0 18px ${tab.accent}30`
                  : "none",
                transition: "all 0.22s ease",
                minWidth: { xs: "auto", sm: 220 },
                "&:hover": {
                  background: active
                    ? `linear-gradient(135deg, ${tab.accent}2e 0%, ${tab.accent}14 100%)`
                    : "rgba(255,255,255,0.06)",
                  borderColor: active ? tab.accent : "rgba(255,255,255,0.18)",
                },
              }}
            >
              {/* Icon badge */}
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  background: active
                    ? `linear-gradient(135deg, ${tab.accent}55, ${tab.accent}22)`
                    : "rgba(255,255,255,0.06)",
                  color: active ? tab.accent : "rgba(255,255,255,0.4)",
                  border: active
                    ? `1px solid ${tab.accent}55`
                    : "1px solid rgba(255,255,255,0.08)",
                  transition: "all 0.22s ease",
                }}
              >
                {tab.icon}
              </Box>

              {/* Text */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.88rem",
                      fontWeight: 700,
                      color: active ? tab.accent : "rgba(255,255,255,0.75)",
                      letterSpacing: 0.3,
                      lineHeight: 1.2,
                      transition: "color 0.22s",
                    }}
                  >
                    {tab.label}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      px: 0.7,
                      py: 0.1,
                      borderRadius: "5px",
                      background: active ? `${tab.accent}33` : "rgba(255,255,255,0.08)",
                      color: active ? tab.accent : "rgba(255,255,255,0.4)",
                      letterSpacing: 0.5,
                      border: active ? `1px solid ${tab.accent}55` : "1px solid transparent",
                      transition: "all 0.22s",
                    }}
                  >
                    {tab.short}
                  </Box>
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    fontSize: "0.72rem",
                    color: "rgba(255,255,255,0.38)",
                    mt: 0.2,
                    letterSpacing: 0.2,
                  }}
                >
                  {tab.desc}
                </Box>
              </Box>

              {/* Mobile: just label */}
              <Box
                component="span"
                sx={{
                  display: { xs: "block", sm: "none" },
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: active ? tab.accent : "rgba(255,255,255,0.6)",
                }}
              >
                {tab.short}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Tab content ───────────────────────────────────────────────── */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>

        {/* VTS Reports */}
        {reportMode === "vts" && (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflowY: "auto", minHeight: 0 }}>
            <VTSReportsView />
          </Box>
        )}

        {/* VA Reports (existing flow) */}
        {reportMode === "va" && (
          <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
            {viewMode === "filter" ? (
              <ReportFilters onApplyFilters={handleApplyFilters} />
            ) : (
              <Box sx={{ px: { xs: 2, sm: 2.5, md: 3 }, py: { xs: 1.5, sm: 2 } }}>
                <ReportHeader
                  onBackToFilter={handleBackToFilter}
                  timeFilter={timeFilter}
                  selectedAreas={selectedAreas}
                  selectedDetections={selectedDetections}
                  selectedPriorities={selectedPriorities}
                  selectedLocationTypes={selectedLocationTypes}
                  selectedCloseStatus={selectedCloseStatus}
                  totalEvents={totalEvents}
                  reportData={reportData}
                />

                {error && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2, backgroundColor: "#2c3e50", color: "white" }}
                  >
                    {error}
                  </Alert>
                )}

                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "50vh",
                    }}
                  >
                    <CircularProgress sx={{ color: "#1ABC9C" }} />
                  </Box>
                ) : (
                  <ReportResults
                    reportData={reportData}
                    selectedDetections={selectedDetections}
                    selectedPriorities={selectedPriorities}
                    selectedCloseStatus={selectedCloseStatus}
                    dataLoaded={dataLoaded}
                    totalEvents={totalEvents}
                    pagination={pagination}
                 
                    isLoadingMore={isLoadingMore}
                    onLoadMore={handleLoadMore}
                    isMobile={isMobile}
                    isTablet={isTablet}
                    typeSpecificCounts={typeSpecificCounts}
                    typeLoadingStatus={isTypeLoadingMore}
                    onTypeSpecificLoadMore={handleTypeSpecificLoadMore}
                    updateEventPriority={updateEventPriority}
                    getEventPriority={getEventPriority}
                    onEventUpdated={onEventUpdated}
                    setReportData={setReportData}
                  />
                )}
              </Box>
            )}
          </Box>
        )}

      </Box>
    </Box>
  );
};

export default ReportsView;
